import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Gmeet, Teams, Zoom } from '@/assets/icons/location';
import { Building07, Phone } from '@kula-ai/icons-core';
import { Hackerrank } from '@/assets/icons/ats';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import { FormControl, FormErrorMessage } from '@chakra-ui/react';
import { FormCustomLabel } from '@/components/formBuilder/components';
import { useField } from 'formik';
import { SelectOptionWithIcon } from '@/components/formBuilder/SelectComponents';
import { SchLocationAvailability } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/useSchLocationField/SchLocationAvailability';
import { KText } from '@/ui-library/components';
import isEqual from 'lodash.isequal';
import { useSelector } from 'react-redux';
import { useIntegrations } from '@/hooks/use-integrations';
import { getAtsJobCreate } from '@/store/atsJobCreate';

// Type for location options
interface LocationOption {
  label: string;
  value: string;
  icon: React.ComponentType<any>;
  isAvailable: boolean;
  availability?: number | boolean;
  isDisabled?: boolean;
}

export const SchLocation = ({
  registerForUpdates,
  updateSharedData,
  interviewPlan,
  formRef,
  interviewDataRef,
  isSelfSchedule,
  locationOptions: locations,
  disabled = false,
  interviewPlatform = null,
}) => {
  const isHackerRankFlow = interviewPlatform === 'hackerrank';
  const { getHackerrankStatus } = useSelector(getAtsJobCreate);
  const noHackerrankAccount = getHackerrankStatus?.error ?? false;
  const { isHackerRankEnabled } = useIntegrations();

  const [localInterviewers, setLocalInterviewers] = useState<any[]>(
    interviewDataRef?.current?.interviewers ?? []
  );

  const [, meta, helpers] = useField('location');
  const { value, error, touched } = meta;
  const { setTouched, setValue, setError } = helpers;
  const isError =
    touched && error && (Array.isArray(value) ? value?.length === 0 : !value);

  const [selectOption, setSelectOption] = useState<LocationOption | undefined>(
    undefined
  );
  const selectRef = useRef<any>(null);
  const hasManuallySelectedLocation = useRef(false);

  useEffect(() => {
    const unregisterInterviewers = registerForUpdates(
      'interviewers',
      (data) => {
        const { interviewers = [] } = data?.fullData ?? {};
        setLocalInterviewers((prev) => {
          if (JSON.stringify(prev) !== JSON.stringify(interviewers)) {
            return interviewers;
          }
          return prev;
        });
      }
    );

    return () => {
      unregisterInterviewers();
    };
  }, [registerForUpdates]);

  const [localInterviewKind, setLocalInterviewKind] = useState(
    interviewDataRef?.current?.interviewKind
  );
  const [localInterviewersDetails, setLocalInterviewersDetails] = useState(
    interviewDataRef?.current?.interviewersDetails || []
  );

  useEffect(() => {
    const unregisterInterviewKind = registerForUpdates(
      'interviewKind',
      (data) => {
        const { interviewKind } = data?.fullData ?? {};

        setLocalInterviewKind((prev) => {
          return isEqual(prev, interviewKind) ? prev : interviewKind;
        });
      }
    );

    return () => {
      unregisterInterviewKind();
    };
  }, [registerForUpdates]);

  useEffect(() => {
    const unregisterInterviewersDetails = registerForUpdates(
      'interviewersDetails',
      (data) => {
        const { interviewersDetails = [] } = data?.fullData ?? {};
        setLocalInterviewersDetails((prev) => {
          return JSON.stringify(prev) !== JSON.stringify(interviewersDetails)
            ? interviewersDetails
            : prev;
        });
      }
    );

    return () => {
      unregisterInterviewersDetails();
    };
  }, [registerForUpdates]);

  // Auto-manage Zoom location based on availability:
  // - If Zoom is selected but disabled → fall back to next available location
  // - If Zoom becomes available → auto-upgrade to Zoom (unless user manually chose another)
  useEffect(() => {
    // HackerRank flow opens this form pre-set to `hackerrank` and the user is
    // expected to keep that platform; the zoom auto-upgrade would otherwise
    // clobber it the moment an interviewer is selected.
    if (isHackerRankFlow) return;

    const { setFieldValue, values } = formRef?.current ?? {};
    const currentLocation = values?.location;
    const anyInterviewerCanHost = Array.isArray(localInterviewersDetails)
      ? localInterviewersDetails.some(
          (i: any) => i?.can_host_zoom_meeting && !i?.zoom_rate_limit_reached
        )
      : false;
    const userCanHostZoom =
      Boolean(interviewPlan?.can_host_zoom_meeting) &&
      !interviewPlan?.zoom_rate_limit_reached;

    const isZoomDisabledSelfScheduleCase =
      localInterviewKind === 'one_on_one' &&
      isSelfSchedule &&
      Array.isArray(localInterviewers) &&
      localInterviewers.length > 1 &&
      !userCanHostZoom;

    const isZoomDisabledNoEligibleHost =
      !anyInterviewerCanHost && !userCanHostZoom;

    const isZoomDisabled =
      isZoomDisabledSelfScheduleCase || isZoomDisabledNoEligibleHost;

    const planHasExplicitNonZoomLocation =
      !!interviewPlan?.location &&
      interviewPlan.location !== 'unspecified' &&
      interviewPlan.location !== 'zoom';

    const gmeetUsable =
      !!locations?.google_meet && locations?.google_meet_enabled !== false;

    if (currentLocation === 'zoom' && isZoomDisabled) {
      // Zoom is selected but can't be used → fall back: Google Meet > Teams > Phone
      const fallbackLocation = gmeetUsable
        ? 'google_meet'
        : locations?.teams
          ? 'teams'
          : 'phone_interview';

      setFieldValue('location', fallbackLocation);
      setFieldValue('host_id', '');
      setSelectOption(null);
      hasManuallySelectedLocation.current = false;

      updateSharedData?.({
        interviewLocation: fallbackLocation,
      });
    } else if (
      !isZoomDisabled &&
      locations?.zoom &&
      currentLocation !== 'zoom' &&
      !hasManuallySelectedLocation?.current &&
      !planHasExplicitNonZoomLocation
    ) {
      // Zoom is now available and user hasn't manually chosen → upgrade to Zoom
      setFieldValue('location', 'zoom');
      setFieldValue('host_id', '');
      setSelectOption(null);

      updateSharedData?.({
        interviewLocation: 'zoom',
      });
    }
  }, [
    localInterviewKind,
    isSelfSchedule,
    localInterviewers,
    interviewPlan,
    formRef,
    localInterviewersDetails,
    locations,
    updateSharedData,
    isHackerRankFlow,
  ]);

  // Fall back off Google Meet when it's selected but disabled at the Workspace
  // level, so the interview never ends up with a missing meeting link.
  useEffect(() => {
    if (isHackerRankFlow) return;

    const { setFieldValue, values } = formRef?.current ?? {};
    const currentLocation = values?.location;
    const gmeetDisabled = locations?.google_meet_enabled === false;

    if (currentLocation === 'google_meet' && gmeetDisabled) {
      const fallbackLocation = locations?.teams ? 'teams' : 'phone_interview';
      setFieldValue('location', fallbackLocation);
      setSelectOption(null);
      hasManuallySelectedLocation.current = false;
      updateSharedData?.({ interviewLocation: fallbackLocation });
    }
  }, [locations, formRef, updateSharedData, isHackerRankFlow]);

  const handleChange = useCallback(
    (selectedOption) => {
      const newValue = selectedOption?.value || null;
      hasManuallySelectedLocation.current = true;
      setSelectOption(selectedOption);
      setTouched(true);
      setError(null);
      setValue(newValue);

      setTimeout(() => {
        // ALWAYS update shared data when location changes
        updateSharedData?.({
          interviewLocation: selectedOption?.value,
        });

        // Determine which field to scroll to based on location type
        let id;
        if (['zoom', 'google_meet', 'teams'].includes(selectedOption?.value)) {
          id = 'note_taker_enabled';
        }

        if (selectedOption?.value === 'onsite') {
          id = 'ats_office_id';
        }
        if (selectedOption?.value === 'hackerrank') {
          id = 'ext_interview_template_id';
        }

        // Scroll to relevant field if there is one
        if (id) {
          const element = document?.getElementById(id);
          if (element) {
            element?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
      }, 100);
    },

    [setTouched, setError, setValue, updateSharedData]
  );

  // Memoized location options with availability and disable logic
  const locationOptions = useMemo<LocationOption[]>(
    () =>
      [
        {
          label: 'Zoom',
          value: 'zoom',
          icon: Zoom,
          isAvailable: !!locations?.zoom,

          isDisabled: (() => {
            const isOneOnOneInterview = localInterviewKind === 'one_on_one';
            const hasMultipleInterviewers =
              Array.isArray(localInterviewers) && localInterviewers.length > 1;

            const anyInterviewerCanHost = Array.isArray(
              localInterviewersDetails
            )
              ? localInterviewersDetails.some(
                  (i: any) =>
                    i?.can_host_zoom_meeting && !i?.zoom_rate_limit_reached
                )
              : false;

            const userCanHostZoom =
              Boolean(interviewPlan?.can_host_zoom_meeting) &&
              !interviewPlan?.zoom_rate_limit_reached;

            const disableForSelfScheduleMultiInt =
              isOneOnOneInterview &&
              isSelfSchedule &&
              hasMultipleInterviewers &&
              !userCanHostZoom;

            const disableWhenNoEligibleHost =
              !anyInterviewerCanHost && !userCanHostZoom;

            return disableForSelfScheduleMultiInt || disableWhenNoEligibleHost;
          })(),
        },
        {
          label: 'Google Meet',
          value: 'google_meet',
          icon: Gmeet,
          isAvailable: !!locations?.google_meet,
          isDisabled: locations?.google_meet_enabled === false,
        },
        {
          label: 'Teams',
          value: 'teams',
          icon: Teams,
          isAvailable: !!locations?.teams,
        },
        {
          label: 'HackerRank',
          value: 'hackerrank',
          icon: Hackerrank,
          isAvailable: !!locations?.hackerrank && !isSelfSchedule,
          isDisabled: !isHackerRankEnabled || noHackerrankAccount,
        },
        {
          label: 'Phone Interview',
          value: 'phone_interview',
          icon: Phone,
          isAvailable: !!locations?.phone_interview,
        },
        {
          label: 'Onsite / Physical',
          value: 'onsite',
          icon: Building07,
          isAvailable: !!locations?.onsite,
        },
      ].filter((option) => option?.isAvailable),
    [
      locations,
      localInterviewKind,
      isSelfSchedule,
      localInterviewers,
      interviewPlan,
      localInterviewersDetails,
      isHackerRankEnabled,
      noHackerrankAccount,
    ]
  );

  // Initialize and update selectOption based on Formik value
  useEffect(() => {
    if (
      (value && !selectOption) ||
      (selectOption && selectOption.value !== value)
    ) {
      const newOption = locationOptions.find((opt) => opt.value === value);
      setSelectOption(newOption || null);
    }
  }, [value, locationOptions, selectOption]);

  return (
    <FormControl
      isRequired
      isInvalid={touched && !!error}
      h="100%"
      margin="0.75em 0"
    >
      <FormCustomLabel
        item={{
          label: 'Location',
          name: 'location',
          labelStyles: { position: 'relative' },
        }}
      />

      <KSelect
        ref={selectRef}
        key={selectOption ? selectOption.value : 'cleared'}
        value={selectOption || null}
        inputId="location"
        isClearable={false}
        isDisabled={disabled}
        options={locationOptions}
        menuPlacement="top"
        hasError={isError}
        formatOptionLabel={SelectOptionWithIcon}
        customComponents={{
          IndicatorSeparator: () => null,
          Option: SchLocationAvailability,
        }}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder="Select location"
      />

      {isError && (
        <FormErrorMessage flexDir="column" alignItems="flex-start">
          <KText color="ats-negative">{error}</KText>
        </FormErrorMessage>
      )}
    </FormControl>
  );
};
